import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { primitiveColors, semanticColors } from '@/theme/tokens';

import type { PicksFilterValue } from '@/utils/ballotPicks';

const HIGHLIGHT_COLOR = '#CED8E2';

interface YourPicksFilterProps {
  value: PicksFilterValue;
  onToggle: () => void;
  /**
   * When true, clicks bubble up to the parent AccordionSummary instead of
   * toggling. Only used by a race's own toggle in the no-hearts collapsed case,
   * where the original spec's "click anywhere on a collapsed header expands and
   * resets to All" behavior is preserved. Otherwise the parent toggles and
   * expands directly.
   */
  bubbleClick?: boolean;
  /**
   * Whether the race or ballot has any liked candidates. When `false` and `value` is
   * `'picks'`, the heart icon stays outlined (instead of filling red) — signals
   * a "no picks yet" state without changing the pill position.
   */
  hasHearts?: boolean;
  disabled?: boolean;
  /** Fixed click-target height with the pill centred; the parent reserves it in
   * layout. Omitted, the target grows 8px past the pill without taking space. */
  touchTargetHeight?: number;
}

/**
 * Two-state pill toggle for the picks filter. Each contest accordion carries
 * one for its own race, and the election header carries the master switch that
 * sets every race on the ballot. Controlled — `value`/`onToggle` are owned by
 * the parent so the filter can interact with the accordion's expansion state
 * (auto-collapse when a race has no liked candidates, reset on re-expand).
 *
 * Layout mirrors the partisan selector: each side is `flex: 1`, so the pill
 * is a static 50% slide. No JS measurement needed — the heart-only side and
 * the "All" side are close enough in natural width that an even split looks
 * fine.
 */
export const YourPicksFilter: React.FC<YourPicksFilterProps> = ({
  value,
  onToggle,
  bubbleClick = false,
  hasHearts = true,
  disabled = false,
  touchTargetHeight,
}) => {
  const { t } = useTranslation('ballot');

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (bubbleClick) {
        // No-hearts collapsed path: let the click bubble to the AccordionSummary
        // so its expand handler runs (and resets the filter to 'all' if needed).
        return;
      }
      // Otherwise the parent's `onToggle` flips the filter and opens what it
      // filters in one step, so the click stops here rather than also toggling
      // the AccordionSummary it sits in.
      e.stopPropagation();
      onToggle();
    },
    [bubbleClick, onToggle]
  );

  const isPicks = value === 'picks';

  return (
    <ButtonBase
      // Render as a div, not a button — this control sits inside a contest or
      // election AccordionSummary, which is already a ``<button>``.
      // Nested ``<button>``s are invalid HTML; React's dev-mode
      // validator logs the entire JSX tree on every render when this
      // happens, which dominates the perf trace. ``role="button"`` +
      // ButtonBase's built-in keydown handling preserves a11y.
      component="div"
      role="button"
      disableRipple
      disabled={disabled}
      onClick={handleClick}
      aria-label={isPicks ? t('contests.filterShowingPicks') : t('contests.filterShowingAll')}
      sx={{
        // Outer click target — grows past the pill via vertical padding, offset
        // by an equal negative margin so the margin-box keeps the pill's own
        // height (no layout shift) while the clickable border-box is taller.
        // `alignSelf: 'center'` keeps it level with the header it sits in,
        // whether the title column is one line or wraps onto two.
        flexShrink: 0,
        alignSelf: 'center',
        ...(touchTargetHeight ? { height: touchTargetHeight } : { my: -1, py: 1 }),
        display: 'flex',
        alignItems: 'center',
        '&:hover': { backgroundColor: 'transparent' },
        // Ripple is off, so ring the pill on keyboard focus (matches the chips).
        '&.Mui-focusVisible > .picks-pill': {
          outline: `2px solid ${primitiveColors.logoPurple}`,
          outlineOffset: '2px',
          boxShadow: `0 0 0 2px ${primitiveColors.white}`,
        },
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <Box
        className="picks-pill"
        sx={{
          // Visible pill — preserves the original look (border, height, clip).
          height: { lg: '18px', xs: '16px' },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '5px',
          border: `1px solid ${semanticColors.overlayWhite30}`,
          // overflow rather than clip-path, which would also clip the focus ring.
          overflow: 'hidden',
        }}
      >
        {/* Sliding pill — fixed 50% width, slides via CSS percentage. */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            // Logical inset so the highlight follows the heart when dir="rtl"
            // reverses the segments.
            insetInlineStart: isPicks ? '0%' : '50%',
            width: '50%',
            borderRadius: '4px',
            backgroundColor: HIGHLIGHT_COLOR,
            transition: 'inset-inline-start 0.25s ease',
            pointerEvents: 'none',
          }}
        />

        {/* Start side — heart only (the aria-label on the ButtonBase still
            announces "your picks" for screen readers, so dropping the visible
            label doesn't lose the semantic). */}
        <Box
          component="span"
          sx={{
            flex: 1,
            position: 'relative',
            zIndex: 1,
            px: 1,
            py: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isPicks ? semanticColors.textHighEmphasis : semanticColors.overlayWhite85,
          }}
        >
          {isPicks && hasHearts ? (
            <FavoriteIcon sx={{ fontSize: 12, color: primitiveColors.red4 }} />
          ) : (
            <FavoriteBorderIcon sx={{ fontSize: 12 }} />
          )}
        </Box>

        {/* End side — "All" */}
        <Box
          component="span"
          sx={{
            flex: 1,
            position: 'relative',
            zIndex: 1,
            px: 1,
            py: '2px',
            color: !isPicks ? semanticColors.textHighEmphasis : semanticColors.overlayWhite85,
            fontFamily: 'inherit',
            fontSize: '0.6875rem',
            fontWeight: 500,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {t('contests.filterAll')}
        </Box>
      </Box>
    </ButtonBase>
  );
};
